import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import DialpadOutlinedIcon from "@mui/icons-material/DialpadOutlined"
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined"
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Collapse from "@mui/material/Collapse"
import Fade from "@mui/material/Fade"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Portal from "@mui/material/Portal"
import Slide from "@mui/material/Slide"
import Typography from "@mui/material/Typography"
import { alpha } from "@mui/material/styles"
import { Device } from "@twilio/voice-sdk"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { environmentIsDevelop } from "../../../common/utils"
import { canAccessCampaignTab } from "../../users/accountRole"
import {
  selectActiveAccountId,
  selectProfile,
  selectProfileLoaded,
} from "../../users/reducer"
import CandidateAvatar from "../components/CandidateAvatar"
import CampaignScriptPreviewDialog from "../settings/CampaignScriptPreviewDialog"
import { CAMPAIGN_SCRIPT_API_PATH } from "../settings/campaignScriptConstants"
import AmdCallControls from "./AmdCallControls"
import { amdStatusChip, shouldUseAmd } from "./amd"
import { CALL_EVENTS } from "./callEvents"
import { useAmdCallStatus } from "./useAmdCallStatus"

const KEYPAD_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
]

function resolveTwilioCallSid(twilioCall) {
  const params = twilioCall?.parameters || {}
  const sid = params.CallSid || params.CallSID || null
  if (sid && String(sid).startsWith("CA")) {
    return sid
  }
  return null
}

export default function GlobalCallBar({
  open,
  candidate,
  onSessionClose,
  registerHangUp,
}) {
  const isLeadCall = Boolean(candidate?.isLead)
  const [twilioDevice, setTwilioDevice] = useState(null)
  const [twilioDeviceReady, setTwilioDeviceReady] = useState(false)
  const [call, setCall] = useState(null)
  const [callMuted, setCallMuted] = useState(false)
  const [callConnecting, setCallConnecting] = useState(true)
  const [speakerVolume, setSpeakerVolume] = useState(0)
  const [keypadOpen, setKeypadOpen] = useState(false)
  const [sentDigits, setSentDigits] = useState("")
  const callRef = useRef(null)
  const callSidRef = useRef(null)
  const closedRef = useRef(false)
  const [activeCallSid, setActiveCallSid] = useState(null)
  const axios = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const activeAccountId = useSelector(selectActiveAccountId)
  const showCampaignScript = canAccessCampaignTab(profile)
  const [scriptPreviewOpen, setScriptPreviewOpen] = useState(false)
  const [scriptContent, setScriptContent] = useState("")

  const amdEnabled = shouldUseAmd(candidate)
  const {
    status: amdStatus,
    statusRef: amdStatusRef,
    override: amdOverride,
    isOverriding: amdOverriding,
    overrideError: amdOverrideError,
  } = useAmdCallStatus({ callSid: activeCallSid, enabled: amdEnabled })

  function captureCallSid(twilioCall) {
    const sid = resolveTwilioCallSid(twilioCall)
    if (sid) {
      callSidRef.current = sid
      setActiveCallSid((current) => (current === sid ? current : sid))
    }
  }

  function handleMicToggleButtonClicked() {
    if (call) {
      const toggleTo = !call.isMuted()
      call.mute(toggleTo)
      setCallMuted(toggleTo)
    }
  }

  function finishCall(callEvent, extraMeta = {}) {
    if (closedRef.current) {
      return
    }
    closedRef.current = true
    const activeCall = callRef.current || call
    captureCallSid(activeCall)
    const callSid = callSidRef.current
    if (activeCall) {
      try {
        activeCall.disconnect()
      } catch (_) {
        // Call may already be disconnected.
      }
    }
    setCall(null)
    callRef.current = null
    setCallMuted(false)
    setCallConnecting(true)
    setSpeakerVolume(0)
    setKeypadOpen(false)
    setSentDigits("")
    setActiveCallSid(null)
    onSessionClose(callEvent, {
      callSid,
      candidateId: candidate?.id,
      amdEnabled,
      amdStatus: amdEnabled ? amdStatusRef.current : null,
      ...extraMeta,
    })
  }

  const finishCallRef = useRef(finishCall)
  finishCallRef.current = finishCall

  useEffect(() => {
    if (!open || !showCampaignScript) {
      setScriptPreviewOpen(false)
      return undefined
    }

    let cancelled = false
    axios
      .get(CAMPAIGN_SCRIPT_API_PATH)
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setScriptContent(data?.content || "")
      })
      .catch(() => {
        if (!cancelled) {
          setScriptContent("")
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, showCampaignScript, axios])

  useEffect(() => {
    if (!registerHangUp) return undefined
    registerHangUp((meta = {}) =>
      finishCallRef.current(CALL_EVENTS.cancelled, meta)
    )
    return () => registerHangUp(null)
  }, [registerHangUp])

  function handleKeypadToggle() {
    setKeypadOpen((prev) => !prev)
  }

  function handleSendDigit(digit) {
    const activeCall = callRef.current || call
    if (!activeCall || callConnecting) {
      return
    }
    try {
      activeCall.sendDigits(digit)
      setSentDigits((prev) => `${prev}${digit}`)
    } catch (_) {
      // Ignore if call ended while sending digits.
    }
  }

  function handleClearDigits() {
    setSentDigits("")
  }

  function handleCallEvent(callEvent) {
    return () => finishCall(callEvent)
  }

  useEffect(() => {
    if (!open || !profileLoaded || (!candidate?.id && !isLeadCall)) {
      return undefined
    }

    let cancelled = false
    let device = null

    setTwilioDeviceReady(false)
    setTwilioDevice(null)
    closedRef.current = false
    setKeypadOpen(false)
    setSentDigits("")

    const tokenUrl = isLeadCall
      ? "/crm/api/twilio/voice-access-token/lead/"
      : `/crm/api/twilio/voice-access-token/${candidate.id}/`

    axios
      .get(tokenUrl)
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        device = new Device(data.token, {
          logLevel: environmentIsDevelop() ? 1 : 5,
          codecPreferences: ["opus", "pcmu"],
        })
        device.on("registered", () => {
          if (!cancelled) {
            setTwilioDeviceReady(true)
          }
        })
        device.on("error", () => {
          if (!cancelled) {
            setTwilioDeviceReady(false)
          }
        })
        device.register()
        setTwilioDevice(device)
      })
      .catch(() => {
        if (!cancelled) {
          setTwilioDeviceReady(false)
          onSessionClose(CALL_EVENTS.cancelled, { skipAttemptLog: true })
        }
      })

    return () => {
      cancelled = true
      setTwilioDeviceReady(false)
      if (device) {
        try {
          device.unregister()
          device.destroy()
        } catch (_) {
          // Device may already be destroyed.
        }
      }
      setTwilioDevice(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profileLoaded, candidate?.id, isLeadCall, axios])

  useEffect(() => {
    if (
      !open ||
      !twilioDeviceReady ||
      !twilioDevice ||
      !candidate?.phone ||
      (!candidate?.id && !isLeadCall)
    ) {
      return undefined
    }

    closedRef.current = false
    callSidRef.current = null
    setActiveCallSid(null)
    setCallConnecting(true)

    let activeCall = null
    let cancelled = false

    async function makeCall() {
      const connectParams = { To: candidate.phone }
      if (activeAccountId) {
        connectParams.AccountOwnerId = String(activeAccountId)
      }
      if (candidate.dialerSessionId) {
        connectParams.DialerSessionId = String(candidate.dialerSessionId)
      }
      if (isLeadCall) {
        connectParams.LeadSource = candidate.leadSource || "mls"
        connectParams.LeadRefId = candidate.leadRefId || ""
        connectParams.LeadName = candidate.candidate_name || ""
        connectParams.LeadPhone = candidate.phone || ""
      }
      activeCall = await twilioDevice.connect({
        params: connectParams,
      })
      if (cancelled) {
        try {
          activeCall.disconnect()
        } catch (_) {
          // Ignore cleanup errors.
        }
        return
      }
      activeCall.on("ringing", () => captureCallSid(activeCall))
      activeCall.on("accept", () => {
        captureCallSid(activeCall)
        setCallConnecting(false)
      })
      activeCall.on("disconnect", handleCallEvent(CALL_EVENTS.disconnected))
      activeCall.on("cancel", handleCallEvent(CALL_EVENTS.cancelled))
      activeCall.on("volume", (inputVolume, outputVolume) =>
        setSpeakerVolume(outputVolume)
      )
      callRef.current = activeCall
      setCall(activeCall)
    }

    makeCall().catch(() => {
      if (!cancelled) {
        finishCall(CALL_EVENTS.cancelled)
      }
    })

    return () => {
      cancelled = true
      if (activeCall) {
        try {
          activeCall.disconnect()
        } catch (_) {
          // Call may already be disconnected.
        }
      }
      callRef.current = null
      setCall(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    twilioDeviceReady,
    twilioDevice,
    candidate?.id,
    candidate?.phone,
    isLeadCall,
    activeAccountId,
  ])

  if (!open || (!candidate?.id && !isLeadCall)) {
    return null
  }

  const officeName = String(
    candidate.company_name || candidate.office_name || ""
  ).trim()

  return (
    <>
      <Portal>
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Paper
            elevation={12}
            sx={{
              position: "fixed",
              bottom: 24,
              right: "calc(var(--power-dialer-sidebar-width, 0px) + 24px)",
              zIndex: 1500,
              width: 360,
              maxWidth: "calc(100vw - 32px)",
              p: 2,
              borderRadius: 2,
              pointerEvents: "auto",
            }}
          >
            <Row gap={1.5} sx={{ alignItems: "center" }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Box
                  sx={{
                    borderRadius: "50%",
                    outline: (theme) =>
                      `3px solid ${alpha(
                        theme.palette.primary.main,
                        speakerVolume
                      )}`,
                    transition: (theme) =>
                      theme.transitions.create(["outlineColor"], {
                        duration: 50,
                      }),
                  }}
                >
                  <CandidateAvatar
                    candidate={candidate}
                    size="52px"
                    fontSize="1.25em"
                  />
                </Box>
                <Fade in={callConnecting}>
                  <CircularProgress
                    variant="indeterminate"
                    color="primary"
                    size={54}
                    thickness={2}
                    sx={{
                      position: "absolute",
                      top: -1,
                      left: -1,
                    }}
                  />
                </Fade>
              </Box>

              <Column sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                  {candidate.candidate_name || "Calling…"}
                </Typography>
                {!callConnecting && officeName ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={officeName}
                    sx={{ fontWeight: 600 }}
                  >
                    {officeName}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary" noWrap>
                  {amdEnabled
                    ? amdStatusChip(amdStatus).label
                    : callConnecting
                    ? "Connecting…"
                    : "On call — navigate freely across the app"}
                </Typography>
              </Column>

              <Row gap={0.5} sx={{ flexShrink: 0 }}>
                <IconButton
                  size="small"
                  aria-label={keypadOpen ? "Hide keypad" : "Show keypad"}
                  disabled={callConnecting}
                  onClick={handleKeypadToggle}
                  sx={(theme) => ({
                    backgroundColor: keypadOpen
                      ? theme.palette.primary.main
                      : theme.palette.colors.gray200,
                    color: keypadOpen ? "white" : "inherit",
                    "&:hover": {
                      backgroundColor: keypadOpen
                        ? theme.palette.primary.dark
                        : theme.palette.action.hover,
                    },
                  })}
                >
                  <DialpadOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={callMuted ? "Unmute" : "Mute"}
                  onClick={handleMicToggleButtonClicked}
                  sx={(theme) => ({
                    backgroundColor: callMuted
                      ? theme.palette.error.main
                      : theme.palette.colors.gray200,
                    "&:hover": {
                      backgroundColor: callMuted
                        ? theme.palette.error.dark
                        : theme.palette.action.hover,
                    },
                  })}
                >
                  {callMuted ? (
                    <MicOffOutlinedIcon
                      fontSize="small"
                      sx={{ color: "white" }}
                    />
                  ) : (
                    <MicNoneOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="End call"
                  onClick={() => finishCall(CALL_EVENTS.disconnected)}
                  sx={(theme) => ({
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: theme.palette.error.dark,
                    },
                  })}
                >
                  <CallEndOutlinedIcon fontSize="small" />
                </IconButton>
              </Row>
            </Row>

            {amdEnabled ? (
              <AmdCallControls
                status={amdStatus}
                onOverride={amdOverride}
                isOverriding={amdOverriding}
                overrideError={amdOverrideError}
              />
            ) : null}

            {showCampaignScript ? (
              <Button
                fullWidth
                size="small"
                variant={scriptPreviewOpen ? "contained" : "outlined"}
                startIcon={<DescriptionOutlinedIcon />}
                onClick={() => setScriptPreviewOpen((prev) => !prev)}
                sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
              >
                Campaign Script
              </Button>
            ) : null}

            <Collapse in={keypadOpen}>
              <Column
                gap={1}
                sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}
              >
                <Row
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {callConnecting
                      ? "Keypad available once connected"
                      : "Press keys for phone menus (IVR)"}
                  </Typography>
                  {sentDigits ? (
                    <Button size="small" onClick={handleClearDigits}>
                      Clear
                    </Button>
                  ) : null}
                </Row>
                {sentDigits ? (
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", letterSpacing: 2 }}
                  >
                    Sent: {sentDigits}
                  </Typography>
                ) : null}
                <Column gap={0.75}>
                  {KEYPAD_KEYS.map((row) => (
                    <Row
                      key={row.join("-")}
                      gap={0.75}
                      sx={{ justifyContent: "center" }}
                    >
                      {row.map((digit) => (
                        <Button
                          key={digit}
                          variant="outlined"
                          disabled={callConnecting}
                          onClick={() => handleSendDigit(digit)}
                          sx={{
                            minWidth: 56,
                            height: 44,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                          }}
                        >
                          {digit}
                        </Button>
                      ))}
                    </Row>
                  ))}
                </Column>
              </Column>
            </Collapse>
          </Paper>
        </Slide>
      </Portal>
      {showCampaignScript ? (
        <CampaignScriptPreviewDialog
          open={open && scriptPreviewOpen}
          onClose={() => setScriptPreviewOpen(false)}
          content={scriptContent}
          inCall
        />
      ) : null}
    </>
  )
}
